import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription, take } from 'rxjs';
import { saveAs } from 'file-saver';

import {
    TTSApiService,
    TTSStreamMessage,
} from 'src/app/core/services/tts-api.service';
import { UserService } from 'src/app/core/services/user.service';

import { environment } from 'src/environments/environment';

import {
    ZtButtonConfig,
    ZtButtonType,
} from 'src/app/shared/components/atoms/zt-button/zt-button.model';
import {
    ZtDropdownOption,
    ZtDropdownConfig,
} from 'src/app/shared/components/atoms/zt-dropdown/zt-dropdown.model';
import { ZtIcon } from 'src/app/shared/components/atoms/zt-icon/zt-icon.model';
import { ZtSliderConfig } from 'src/app/shared/components/atoms/zt-slider/zt-slider.model';
import { ZtTextAreaConfig } from 'src/app/shared/components/atoms/zt-textarea/zt-textarea.model';
import { ZtToggleConfig } from 'src/app/shared/components/atoms/zt-toggle/zt-toggle.model';
import { ZtLoaderService } from 'src/app/shared/components/molecules/zt-loader/zt-loader.service';
import { ExecutionService } from 'src/app/core/services/execution.service';
import { MessageService } from 'primeng/api';
import { EventEmitterService } from 'src/app/core/services/event-emitter.service';

export interface ExecutionParams {
    text: string;
    language: string;
    voice: string;
    pace?: string;
    pitch?: string;
    requested_at: string;
}

@UntilDestroy()
@Component({
    selector: 'zt-home',
    templateUrl: 'home.component.html',
    styleUrls: ['home.component.scss'],
})
export class HomeComponent implements AfterViewInit, OnDestroy {
    blob: Blob | undefined;
    fileToDownload: Blob | undefined;
    text =
        'Înlocuiți textul din acest chenar cu orice alt text și apăsați "Play". De asemenea, puteți selecta diferite voci.';
    languageOptions: ZtDropdownOption[] = [{ id: 'ro', value: 'Romanian' }];
    languageConfig: ZtDropdownConfig = {
        id: 'language',
        label: 'Language',
        name: 'language',
    };
    voiceOptions: ZtDropdownOption[] = [];
    voiceConfig: ZtDropdownConfig = {
        id: 'voice',
        label: 'Voice',
        name: 'voice',
    };
    paceConfig: ZtSliderConfig = {
        id: 'pace',
        label: 'Pace',
        name: 'pace',
    };
    pitchConfig: ZtSliderConfig = {
        id: 'pitch',
        label: 'Pitch',
        name: 'pitch',
    };
    streamingConfig: ZtToggleConfig = {
        id: 'streaming',
        label: 'Enable Streaming',
        name: 'streaming',
    };
    phonemsConfig: ZtTextAreaConfig = {
        id: 'phonems',
        label: 'Phonems',
        name: 'phonems',
    };
    transliteralsConfig: ZtTextAreaConfig = {
        id: 'transliterals',
        label: 'Transliterals',
        name: 'transliterals',
    };
    playBtnConfig: ZtButtonConfig = {
        label: 'Play',
        icon: ZtIcon.PLAY,
        name: 'play',
    };
    stopBtnConfig: ZtButtonConfig = {
        label: 'Stop',
        icon: ZtIcon.STOP,
        name: 'stop',
    };
    saveBtnConfig: ZtButtonConfig = {
        label: 'Add to history',
        icon: ZtIcon.ADD_DOCUMENT,
        name: 'download',
    };
    downloadBtnConfig: ZtButtonConfig = {
        label: 'Download',
        icon: ZtIcon.DOWNLOAD,
        name: 'download',
    };

    ttsForm = new FormGroup({
        text: new FormControl<string>(this.text, Validators.required),
        language: new FormControl<string>('ro', Validators.required),
        voice: new FormControl<string>('', Validators.required),
        pace: new FormControl<string>(''),
        pitch: new FormControl<string>(''),
        phonems: new FormControl<string>(''),
        transliterals: new FormControl<string>(''),
        streaming: new FormControl<boolean>(false),
    });

    isFetchingAudio = false;
    isPlayingAudio = false;
    isPlayActive = false;
    usedConfig: ExecutionParams = {
        text: '',
        language: '',
        voice: '',
        pace: '',
        pitch: '',
        requested_at: '',
    };
    hasUserConfig: boolean = false;

    ttsSubscription = new Subscription();
    private audioChunks: Blob[] = [];

    constructor(
        private userService: UserService,
        private executionService: ExecutionService,
        private loadingService: ZtLoaderService,
        private ttsService: TTSApiService,
        private messageService: MessageService,
        private eventEmitterService: EventEmitterService
    ) {}

    ngAfterViewInit(): void {
        // Subscribe to the streaming messages
        this.ttsService
            .getStream()
            .pipe(untilDestroyed(this))
            .subscribe((message: TTSStreamMessage) => {
                console.log(
                    '[HomeComponent]: Stream message received. Type:',
                    message.type,
                    'Has data:',
                    !!message.data,
                    'Has voices:',
                    !!message.voices,
                    'Error:',
                    message.error
                );
                this.handleStreamMessage(message);
            });

        // Keep connection subscription for voices list
        this.ttsService
            .getConnection()
            .pipe(untilDestroyed(this))
            .subscribe((connection: any) => {
                console.log(
                    '[HomeComponent]: Connection subject updated. Has connection:',
                    !!connection
                );
                if (connection) {
                    this.ttsSubscription.unsubscribe();
                    this.ttsSubscription = connection.subscribe({
                        next: (msg: any) => {
                            console.log(
                                '[HomeComponent]: WebSocket message received. Type:',
                                typeof msg,
                                'Is Blob:',
                                msg instanceof Blob,
                                'Is ArrayBuffer:',
                                msg instanceof ArrayBuffer
                            );
                            // Handle binary data (Blob) from WebSocket
                            if (msg instanceof Blob) {
                                console.log(
                                    '[HomeComponent]: Received Blob. Size:',
                                    msg.size
                                );
                                this.audioChunks.push(msg);
                                this.isFetchingAudio = true;
                            } else if (typeof msg === 'string') {
                                console.log(
                                    '[HomeComponent]: Received string message:',
                                    msg.substring(0, 200)
                                );
                                // Try to parse as JSON
                                try {
                                    const parsed = JSON.parse(msg);
                                    console.log(
                                        '[HomeComponent]: Parsed JSON message:',
                                        parsed
                                    );
                                    this.handleJsonMessage(parsed);
                                } catch (e) {
                                    // Not JSON, could be end of stream indicator
                                    console.log(
                                        '[HomeComponent]: String is not JSON, finalizing audio stream'
                                    );
                                    this.finalizeAudioStream();
                                }
                            } else {
                                // Already parsed JSON object
                                console.log(
                                    '[HomeComponent]: Received object message:',
                                    msg
                                );
                                this.handleJsonMessage(msg);
                            }
                        },
                        error: (error: any) => {
                            console.error(
                                '[HomeComponent]: WebSocket error:',
                                error
                            );
                            this.messageService.add({
                                severity: 'error',
                                summary:
                                    'Could not establish connection to the server.',
                            });
                            this.isFetchingAudio = false;
                            this.isPlayActive = false;
                            this.isPlayingAudio = false;
                            this.audioChunks = [];
                            this.loadingService.hideLoader();
                        },
                        complete: () => {
                            console.log(
                                '[HomeComponent]: WebSocket subscription completed'
                            );
                            // WebSocket closed - finalize audio if we have chunks
                            this.finalizeAudioStream();
                        },
                    });
                } else {
                    console.log(
                        '[HomeComponent]: No connection, unsubscribing'
                    );
                    this.ttsSubscription?.unsubscribe();
                }
            });

        this.ttsService.connect();
        this.checkAvailableVoice();

        this.eventEmitterService
            .getEmitter('onLogout')
            .pipe(untilDestroyed(this))
            .subscribe((data: any) => {
                this.isPlayActive = false;
                this.isPlayingAudio = false;
                this.blob = undefined;
                this.ttsService.connect();
                this.checkAvailableVoice();
            });

        if (this.isLoggedIn()) {
            this.userService
                .getUserConfig()
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (data: any) => {
                        this.phonemsControl?.setValue(data.phonems);
                        this.transliteralsControl?.setValue(data.transliterals);
                        this.hasUserConfig = true;
                    },
                    error: (error: any) => {
                        this.hasUserConfig = false;
                        if (error.status != 404) {
                            this.messageService.add({
                                severity: 'error',
                                summary:
                                    'Could not retrieve the user dictionaries.',
                            });
                        }
                    },
                });
        }
    }

    onSubmitListener(event: Event): void {
        event.preventDefault();
    }

    checkAvailableVoice() {
        console.log('[HomeComponent]: checkAvailableVoice() called');
        this.loadingService.showLoader();
        try {
            const currentUser = this.userService.getCurrentUser();
            console.log(
                '[HomeComponent]: Current user:',
                currentUser ? 'exists' : 'null'
            );
            const message = {
                config: {
                    get_voices: currentUser
                        ? currentUser?.apiKey
                        : environment.API_KEY,
                },
            };
            console.log('[HomeComponent]: Sending get_voices message');
            this.ttsService.sendMessage(message);
        } catch (error) {
            console.error(
                '[HomeComponent]: Error in checkAvailableVoice:',
                error
            );
            this.messageService.add({
                severity: 'error',
                summary:
                    'Could not fetch the list of available voices from the server.',
            });
            this.loadingService.hideLoader();
        }
    }

    onPlayListener(): void {
        if (this.ttsForm.valid) {
            this.ttsService.connect();
            this.blob = undefined;
            this.audioChunks = []; // Reset audio chunks for new request
            this.isPlayActive = true;
            const currentUser = this.userService.getCurrentUser();
            // Get pace value - default to 1 if not set
            const paceValue = this.ttsForm.value.pace;
            const pace =
                paceValue === '' ||
                paceValue === null ||
                paceValue === undefined
                    ? '1'
                    : String(paceValue);

            // Get pitch value - default to 0 if not set
            const pitchValue = this.ttsForm.value.pitch;
            const pitch =
                pitchValue === '' ||
                pitchValue === null ||
                pitchValue === undefined
                    ? '0'
                    : String(pitchValue);

            const task = [
                {
                    text: this.ttsForm.value.text,
                },
                {
                    voice: this.ttsForm.value.voice,
                },
                {
                    pace: pace,
                },
                {
                    pitch: pitch,
                },
                {
                    key: currentUser ? currentUser.apiKey : environment.API_KEY,
                },
                {
                    streaming: this.ttsForm.value.streaming ?? false,
                },
            ];
            this.usedConfig = {
                text: this.ttsForm.value.text!,
                language: this.ttsForm.value.language!,
                voice: this.ttsForm.value.voice!,
                pace: this.ttsForm.value.pace!,
                pitch: this.ttsForm.value.pitch!,
                requested_at: new Date().toISOString(),
            };

            if (this.isLoggedIn()) {
                this.saveUserConfig(task);
            } else {
                this.isFetchingAudio = true;
                this.ttsService.sendMessage({
                    task,
                });
            }
        }
    }

    saveUserConfig(task: any) {
        this.loadingService.showLoader();
        const action = this.hasUserConfig
            ? this.userService.saveUserConfig(
                  this.ttsForm.value.phonems!,
                  this.ttsForm.value.transliterals!
              )
            : this.userService.addUserConfig(
                  this.ttsForm.value.phonems!,
                  this.ttsForm.value.transliterals!
              );
        action.pipe(take(1)).subscribe({
            next: (data: any) => {
                this.isFetchingAudio = true;
                this.ttsService.sendMessage({
                    task,
                });
                this.loadingService.hideLoader();
            },
            error: (error: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Could not save the user dictionaries.',
                });
                this.isPlayActive = false;
                this.loadingService.hideLoader();
            },
        });
    }

    onStopListener(): void {
        this.isPlayActive = false;
        this.isPlayingAudio = false;
    }

    onFinishListener(): void {
        this.isPlayActive = false;
        this.isPlayingAudio = false;
    }

    onPauseListener(): void {
        this.isPlayActive = false;
        this.isPlayingAudio = false;
    }

    saveExecution(): void {
        this.loadingService.showLoader();
        const formData = new FormData();
        formData.append('text', this.usedConfig.text);
        formData.append('language', this.usedConfig.language);
        formData.append('voice', this.usedConfig.voice);
        formData.append('requested_at', this.usedConfig.requested_at);
        formData.append('audio_file', this.fileToDownload!);
        this.executionService
            .saveExecution(formData)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (data: any) => {
                    this.loadingService.hideLoader();
                    this.messageService.add({
                        severity: 'success',
                        summary:
                            'Added the execution successfully to the history.',
                    });
                },
                error: (error: any) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Could not add the execution to the history.',
                    });
                    this.loadingService.hideLoader();
                },
            });
    }

    downloadAudioFile(): void {
        if (this.fileToDownload) {
            saveAs(this.fileToDownload, 'tts-sample');
        }
    }

    isLoggedIn(): boolean {
        return this.userService.isAuthenticated();
    }

    ngOnDestroy(): void {
        this.ttsSubscription.unsubscribe();
        this.ttsService.close();
    }

    get ZtButtonType(): typeof ZtButtonType {
        return ZtButtonType;
    }

    get textControl(): AbstractControl<string | null, string | null> | null {
        return this.ttsForm.get('text');
    }

    get languageControl(): AbstractControl<
        string | null,
        string | null
    > | null {
        return this.ttsForm.get('language');
    }

    get voiceControl(): AbstractControl<string | null, string | null> | null {
        return this.ttsForm.get('voice');
    }

    get paceControl(): AbstractControl<string | null, string | null> | null {
        return this.ttsForm.get('pace');
    }

    get pitchControl(): AbstractControl<string | null, string | null> | null {
        return this.ttsForm.get('pitch');
    }

    get phonemsControl(): AbstractControl<string | null, string | null> | null {
        return this.ttsForm.get('phonems');
    }

    get transliteralsControl(): AbstractControl<
        string | null,
        string | null
    > | null {
        return this.ttsForm.get('transliterals');
    }

    private handleStreamMessage(message: TTSStreamMessage): void {
        console.log(
            '[HomeComponent]: handleStreamMessage. Type:',
            message.type
        );
        switch (message.type) {
            case 'chunk':
                // Individual chunk received - handled in WebSocket subscription
                console.log(
                    '[HomeComponent]: Chunk received in stream (handled in subscription)'
                );
                break;
            case 'complete':
                // All chunks received
                console.log(
                    '[HomeComponent]: Complete message received. Has data:',
                    !!message.data
                );
                if (message.data) {
                    this.blob = message.data;
                    this.fileToDownload = new Blob([message.data], {
                        type: 'audio/wav',
                    });
                    this.isFetchingAudio = false;
                    this.isPlayingAudio = true;
                }
                break;
            case 'error':
                console.error(
                    '[HomeComponent]: Error message received:',
                    message.error
                );
                this.messageService.add({
                    severity: 'error',
                    summary: message.error || 'An error occurred',
                });
                this.isFetchingAudio = false;
                this.isPlayActive = false;
                this.isPlayingAudio = false;
                this.loadingService.hideLoader();
                break;
            case 'voices':
                console.log(
                    '[HomeComponent]: Voices message received. Count:',
                    message.voices?.length
                );
                if (message.voices) {
                    this.voiceOptions = message.voices.map((voice: any) => {
                        if (voice['default'] == 1) {
                            this.ttsForm.controls.voice.setValue(voice['id']);
                        }
                        return {
                            id: voice['id'],
                            value: voice['name'],
                        } as ZtDropdownOption;
                    });
                }
                this.loadingService.hideLoader();
                break;
            default:
                console.warn(
                    '[HomeComponent]: Unknown message type:',
                    (message as any).type
                );
        }
    }

    private handleJsonMessage(msg: any): void {
        console.log(
            '[HomeComponent]: handleJsonMessage called. Has error:',
            !!msg?.error,
            'Has voices:',
            !!msg?.voices
        );
        if (msg?.error) {
            console.error('[HomeComponent]: Server returned error:', msg.error);
            this.messageService.add({
                severity: 'error',
                summary: msg.error,
            });
            this.isFetchingAudio = false;
            this.isPlayActive = false;
            this.isPlayingAudio = false;
            this.audioChunks = [];
            this.loadingService.hideLoader();
        } else if (msg?.voices) {
            const voices: any[] = msg.voices;
            console.log(
                '[HomeComponent]: Processing voices list. Count:',
                voices.length
            );
            this.voiceOptions = voices.map((voice: any) => {
                if (voice['default'] == 1) {
                    this.ttsForm.controls.voice.setValue(voice['id']);
                }
                return {
                    id: voice['id'],
                    value: voice['name'],
                } as ZtDropdownOption;
            });
            this.isFetchingAudio = false;
            this.isPlayActive = false;
            this.isPlayingAudio = false;
            this.loadingService.hideLoader();
        }
    }

    private finalizeAudioStream(): void {
        console.log(
            '[HomeComponent]: finalizeAudioStream called. Audio chunks count:',
            this.audioChunks.length
        );
        if (this.audioChunks.length > 0) {
            const completeBlob = new Blob(this.audioChunks, {
                type: 'audio/wav',
            });
            console.log(
                '[HomeComponent]: Creating complete blob. Size:',
                completeBlob.size
            );
            this.blob = completeBlob;
            this.fileToDownload = new Blob([completeBlob], {
                type: 'audio/wav',
            });
            this.isFetchingAudio = false;
            this.isPlayingAudio = true;
            this.audioChunks = [];
        } else {
            console.warn(
                '[HomeComponent]: finalizeAudioStream called but no audio chunks available'
            );
        }
    }
}
